import { searchOutcomes, advertiserKeysSeen } from "./reportCore";
import { UNSORTED_NICHE_NAME } from "./niches";
import { outcodeOf } from "../locations/radius";

// The niche roll-up behind the Audits page and the Industry Audit unlock: per
// niche, what the account's Snapshots have gathered so far, against the
// unlock targets. Searches are worked out by the same code as the reports
// (searchOutcomes, advertiserKeysSeen in reportCore.js), so the numbers can't
// drift from them. Loaded by core/api/nicheRollupLoader.js; the MCP can use both.
// Pure: no React, no database, no browser, no clock (now is passed in).

// The app_settings row holding the targets.
export const UNLOCK_SETTING_KEY = "industry_audit_unlock";
export const TARGETS = ["searches", "keywords", "locations"];
// "Furthest from target" = the lowest share done; ties go to the first here.
const TIE_ORDER = ["locations", "keywords", "searches"];
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

// { searches, keywords, locations } from the setting's value, or null if it's
// missing or any target isn't a whole number above 0. Null = every niche locked.
export function readUnlockTargets(value) {
  if (!value || typeof value !== "object") return null;
  const targets = {};
  for (const t of TARGETS) {
    if (!Number.isInteger(value[t]) || value[t] <= 0) return null;
    targets[t] = value[t];
  }
  return targets;
}

// The location a search counts under: its postcode district, so "S1 2AB" and
// "S1" are one location. Anything that isn't a UK postcode counts as its own
// text for now (exact postcodes and other countries come later).
export function districtOf(postcode) {
  const text = String(postcode || "").trim().toUpperCase().replace(/\s+/g, " ");
  if (!text) return null;
  return outcodeOf(text) || text;
}

// { searches: { done, target, percent, met }, ... } or null without targets.
// percent is 0-1, capped at 1. Exactly on target is met.
export function targetProgress(counts, targets) {
  if (!targets) return null;
  const progress = {};
  for (const t of TARGETS) {
    const done = counts[t];
    progress[t] = { done, target: targets[t], percent: Math.min(1, done / targets[t]), met: done >= targets[t] };
  }
  return progress;
}

// The target furthest from being met, or null when all are met (or no targets).
export function furthestTarget(progress) {
  if (!progress) return null;
  let furthest = null;
  for (const t of TIE_ORDER) {
    if (progress[t].met) continue;
    if (furthest === null || progress[t].percent < progress[furthest].percent) furthest = t;
  }
  return furthest;
}

// data: {
//   jobs:           [{ id, niche_id, niches: { name }, status, keywords, locations, devices,
//                      samples_per_run, rounds, completed_units, skipped_units, started_at, completed_at }]
//   samples:        [{ id, job_id, run_id, sample_number, completed_units, skipped_units,
//                      planned_at, actual_started_at, actual_finished_at }]
//   searchRecords:  [{ job_id, sample_id, keyword, postcode, device, status, finished_at }]
//   searchKeys:     [{ job_id, sample_id, round, keyword, location, device }] one per search with results
//   advertiserRows: [{ job_id, channel, business_domain, raw_name, raw_data }]
//   keywordRows:    [{ job_id, raw_text, standard_form }]
//   listingLinks, districts: [{ outcode, lat, lng }], setting (the app_settings value or null),
//   complete: false if any part came back short
// }
// Returns { targets, targetsLoaded, complete, niches: [...] }, most searches first.
// Snapshots with no niche are left out. Unsorted is counted but never unlocks.
export function nicheRollup(data, now) {
  const targets = readUnlockTargets(data.setting);
  const weekStart = Date.parse(now) - WEEK_MS;
  const samples = byJob(data.samples);
  const records = byJob(data.searchRecords);
  const keys = byJob(data.searchKeys);
  const advertiserRows = byJob(data.advertiserRows);
  const keywordRows = byJob(data.keywordRows);
  const positions = new Map((data.districts || []).map(d => [d.outcode, d]));

  const niches = new Map();
  for (const job of data.jobs || []) {
    if (!job.niche_id) continue;
    if (!niches.has(job.niche_id)) niches.set(job.niche_id, newNiche(job));
    const n = niches.get(job.niche_id);
    n.snapshots++;
    if (job.status === "running") n.gatheringNow++;

    const jobSamples = samples.get(job.id) || [];
    const sampleById = new Map(jobSamples.map(s => [s.id, s]));
    const standard = standardForms(keywordRows.get(job.id));
    for (const search of searchOutcomes(job, jobSamples, keys.get(job.id), records.get(job.id))) {
      if (search.status !== "completed") continue;
      n.searches++;
      if (search.inferred) n.estimated = true;
      const at = searchDate(search, sampleById.get(search.group), job);
      n.insightSince = earliest(n.insightSince, at);
      if (at && Date.parse(at) > weekStart) n.searchesThisWeek++;
      n.keywords.add(standard.get(search.keyword) || tidyText(search.keyword));
      const district = districtOf(search.postcode);
      if (district) n.locations.set(district, latest(n.locations.get(district), at));
    }
    for (const key of advertiserKeysSeen({ appearances: advertiserRows.get(job.id) || [], listingLinks: data.listingLinks })) {
      n.advertisers.add(key);
    }
  }

  const list = [...niches.values()].map(n => {
    const counts = { searches: n.searches, keywords: n.keywords.size, locations: n.locations.size };
    const progress = targetProgress(counts, targets);
    return {
      nicheId: n.nicheId,
      name: n.name,
      unsorted: n.unsorted,
      snapshots: n.snapshots,
      ...counts,
      advertisers: n.advertisers.size,
      estimated: n.estimated,
      insightSince: n.insightSince,
      gatheringNow: n.gatheringNow,
      searchesThisWeek: n.searchesThisWeek,
      targets,
      progress,
      unlocked: Boolean(progress) && !n.unsorted && TARGETS.every(t => progress[t].met),
      furthest: furthestTarget(progress),
      // The coverage map: districts with a position only (all still count above).
      locationsCovered: [...n.locations]
        .filter(([district]) => positions.has(district))
        .map(([district, lastSearchedAt]) => ({ district, lat: positions.get(district).lat, lng: positions.get(district).lng, lastSearchedAt }))
        .sort((a, b) => a.district.localeCompare(b.district)),
    };
  });
  list.sort((a, b) => b.searches - a.searches || a.name.localeCompare(b.name));

  return { targets, targetsLoaded: Boolean(targets), complete: data.complete !== false, niches: list };
}

function newNiche(job) {
  const name = (job.niches && job.niches.name) || "Unnamed niche";
  return {
    nicheId: job.niche_id, name, unsorted: name === UNSORTED_NICHE_NAME,
    snapshots: 0, gatheringNow: 0, searches: 0, searchesThisWeek: 0, estimated: false, insightSince: null,
    keywords: new Set(), locations: new Map(), advertisers: new Set(),
  };
}

function byJob(rows) {
  const out = new Map();
  for (const row of rows || []) {
    if (!out.has(row.job_id)) out.set(row.job_id, []);
    out.get(row.job_id).push(row);
  }
  return out;
}

// typed keyword -> its tidied standard form, from the job's keyword rows.
function standardForms(rows) {
  return new Map((rows || []).filter(r => r.raw_text && r.standard_form).map(r => [r.raw_text, r.standard_form]));
}

// A keyword with no keyword row (shouldn't happen for niche Snapshots).
function tidyText(keyword) {
  return String(keyword || "").trim().toLowerCase().replace(/\s+/g, " ");
}

// When a completed search ran: its recorded finish, else its sample's times,
// else the Snapshot's (older Snapshots: an estimate, like their counts).
function searchDate(search, sample, job) {
  return search.finishedAt
    || (sample && (sample.actual_finished_at || sample.actual_started_at || sample.planned_at))
    || job.completed_at || job.started_at || null;
}

function earliest(a, b) {
  if (!a) return b || null;
  if (!b) return a;
  return Date.parse(b) < Date.parse(a) ? b : a;
}

function latest(a, b) {
  if (!a) return b || null;
  if (!b) return a;
  return Date.parse(b) > Date.parse(a) ? b : a;
}
